import classnames from 'classnames';
import React, { AnchorHTMLAttributes, forwardRef } from 'react';

import '@eika/button-style/src/LinkButton.scss';
import '@eika/button-style/src/button.scss';
import { Icon, IconTypes } from '@eika/icon';
import '@eika/link-style';

import LinkType, { LinkSizes, LinkTypes } from './LinkType';
import { isIconOnly, isStandardLink, mapClassNames, mapLinkTypes } from './mapper';

export type BasicLinkProps = AnchorHTMLAttributes<HTMLAnchorElement>;

interface LinkProps extends BasicLinkProps {
    className?: string;
    icon?: IconTypes;
    // @default 'link'
    linkType?: LinkType | LinkTypes;
    /**
     * Define size of link.
     * Only usable with linkTypes: 'button' and 'link'
     * @default 'standard'
     */
    size?: LinkSizes;
    // Can only be used in linkType 'link'
    iconOnRightSide?: boolean;
    /**
     * Used when on dark backgrounds, only useable with 'button' and 'link' types.
     * @default false
     */
    inverted?: boolean;
}

const Link = forwardRef<HTMLAnchorElement, LinkProps>(
    (
        {
            children,
            icon,
            className,
            linkType = 'link',
            size = 'standard',
            iconOnRightSide = false,
            inverted = false,
            ...rest
        },
        ref,
    ) => {
        const { type: targetLinkType, size: targetLinkSize } = mapLinkTypes(linkType, size);
        const isIconOnlyLink = isIconOnly(targetLinkType);
        return (
            <a
                className={classnames(
                    'sgw-link',
                    'sgw-button-common',
                    className,
                    mapClassNames(targetLinkType, targetLinkSize),
                    {
                        'button--link__icon-right': iconOnRightSide && isStandardLink(targetLinkType),
                        inverted,
                    },
                )}
                ref={ref}
                {...rest}
            >
                <span
                    className={classnames('sgw-link__text', {
                        'sgw-link__text--icon-visible': icon && !iconOnRightSide,
                        'icon-right': icon && iconOnRightSide,
                    })}
                >
                    {targetLinkType === 'icon' && icon && (
                        <span className="sgw-button__icon-container">
                            <Icon type={icon} />
                        </span>
                    )}
                    {!iconOnRightSide && icon && !isIconOnlyLink && <Icon className="icon--22" type={icon} />}
                    {targetLinkType === 'return' && (
                        <Icon
                            type={IconTypes.PIL_VENSTRE}
                            className={classnames('icon--20', 'sgw-link__return-link-arrow-icon')}
                        />
                    )}
                    {children}
                    {isStandardLink(targetLinkType) && iconOnRightSide && icon && (
                        <Icon type={icon} className="icon--22" />
                    )}
                </span>
            </a>
        );
    },
);

export default Link;
